import React, { useState } from 'react';
import { LABEL_TEXT, TEXTAREA_BASE } from '../styles/classNames';

/**
 * Компонент для редактирования одного промпта
 */
export function PromptEditor({ prompt, onSave, onReset, disabled }) {
  // Защита от отсутствующих данных
  if (!prompt || typeof prompt !== 'object') {
    console.error('PromptEditor: некорректный prompt:', prompt);
    return (
      <div className="border border-red-200 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
        <p className="text-red-600 dark:text-red-400">Ошибка: некорректные данные промпта</p>
      </div>
    );
  }
  
  const [customPrompt, setCustomPrompt] = useState(prompt.custom_prompt || '');
  const [useCustom, setUseCustom] = useState(Boolean(prompt.use_custom));
  const [isEditing, setIsEditing] = useState(false);

  // Маппинг типов промптов на человекочитаемые названия
  const promptTypeLabels = {
    audio_transcription: '🎤 Аудио транскрибация',
    text_processing: '📝 AI обработка текста',
    photo_ocr: '📷 OCR фото'
  };

  const handleSave = async () => {
    await onSave(prompt.prompt_type, {
      custom_prompt: customPrompt || null,
      use_custom: useCustom
    });
    setIsEditing(false);
  };

  const handleReset = async () => {
    if (window.confirm('Вы уверены что хотите сбросить этот промпт к оригинальному значению?')) {
      await onReset(prompt.prompt_type);
      setCustomPrompt('');
      setUseCustom(false);
      setIsEditing(false);
    }
  };

  const handleToggleUseCustom = (checked) => {
    setUseCustom(checked);
    if (checked && !customPrompt) {
      // Если включаем кастомный, но его нет - копируем дефолтный
      setCustomPrompt(prompt.default_prompt || '');
      setIsEditing(true);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {promptTypeLabels[prompt.prompt_type] || prompt.prompt_type}
        </h3>
        <div className="flex items-center gap-2">
          {/* Чекбокс "Использовать мой промпт" */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useCustom}
              onChange={(e) => handleToggleUseCustom(e.target.checked)}
              disabled={disabled}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Использовать мой промпт
            </span>
          </label>
        </div>
      </div>

      {/* Дефолтный промпт (всегда read-only) */}
      <div>
        <label className={LABEL_TEXT}>
          📋 Оригинальный промпт (неизменяемый)
        </label>
        <textarea
          value={prompt.default_prompt || ''}
          readOnly
          rows={8}
          className={`${TEXTAREA_BASE} bg-gray-50 dark:bg-gray-800 cursor-not-allowed text-xs font-mono`}
        />
      </div>

      {/* Кастомный промпт (редактируемый) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className={LABEL_TEXT}>
            ✏️ Мой промпт {!useCustom && <span className="text-gray-400">(не используется)</span>}
          </label>
          {!isEditing && customPrompt && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
            >
              Редактировать
            </button>
          )}
        </div>
        
        {isEditing || !customPrompt ? (
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            disabled={disabled}
            rows={8}
            placeholder="Введите свой промпт или скопируйте оригинальный для модификации..."
            className={`${TEXTAREA_BASE} text-xs font-mono ${!useCustom ? 'opacity-50' : ''}`}
          />
        ) : (
          <div 
            onClick={() => !disabled && setIsEditing(true)}
            className={`${TEXTAREA_BASE} min-h-[200px] bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-mono whitespace-pre-wrap ${!useCustom ? 'opacity-50' : ''}`}
          >
            {customPrompt || <span className="text-gray-400 italic">Промпт не задан. Нажмите чтобы добавить.</span>}
          </div>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {(customPrompt || '').length} символов
        </div>
      </div>

      {/* Кнопки действий */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || (!useCustom && !customPrompt)}
          className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          💾 Сохранить
        </button>
        
        <button
          type="button"
          onClick={handleReset}
          disabled={disabled || (!customPrompt && !useCustom)}
          className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          ↩️ Восстановить оригинальный
        </button>
      </div>

      {/* Индикатор активного промпта */}
      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          <strong>Сейчас используется:</strong>{' '}
          <span className={useCustom ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}>
            {useCustom ? '✏️ Мой кастомный промпт' : '📋 Оригинальный промпт'}
          </span>
        </div>
      </div>
    </div>
  );
}
